import { createContext, useContext, useEffect, useState } from 'react';

import { marshallItem, selectId } from './helpers';
import {
  CreateContextResponse,
  CurrentSelection,
  FolderableItem,
  FolderableFolder,
  SelectionContextState,
  SelectionParams,
  SelectionTypes,
} from './types';

export function createSelectionContext<
  ContentId = number,
  ContentName = string,
>(): CreateContextResponse<ContentId, ContentName> {
  const SelectionContext = createContext<SelectionContextState<ContentId, ContentName>>({
    bulk: [],
    toggleBulkSelection: () => undefined,
    addPageToBulk: () => undefined,
    removePageFromBulk: () => undefined,
    active: [],
    selectItem: () => undefined,
    selectBulk: () => undefined,
    clearAll: () => undefined,
  });

  const useContextHook = () => useContext(SelectionContext);

  return [SelectionContext, useContextHook];
}

export function useSelectionContext<ContentId = number, ContentName = string>({
  folderId,
  folders,
  contentItems,
}: SelectionParams<ContentId, ContentName>) {
  const [bulkSelection, setBulkSelection] = useState<CurrentSelection<ContentId, ContentName>[]>(
    [],
  );
  const [currentSelection, setCurrentSelection] = useState<
    CurrentSelection<ContentId, ContentName>[]
  >([]);

  const toggleBulkSelection = (item: FolderableItem<ContentId, ContentName> | FolderableFolder) => {
    setBulkSelection((currentValue) => {
      if (currentValue.findIndex((value) => value.id === item.id) === -1) {
        return [...currentValue, marshallItem(item)];
      }

      return currentValue.filter((value) => value.id !== item.id);
    });
  };

  const addPageToBulk = () => {
    setBulkSelection((currentValue) => {
      const newValue = [...currentValue];
      const addItem = (item: FolderableItem<ContentId, ContentName> | FolderableFolder) => {
        if (currentValue.findIndex((value) => value.id === item.id) === -1) {
          newValue.push(marshallItem(item));
        }
      };

      folders.forEach(addItem);
      contentItems.forEach(addItem);

      return newValue;
    });
  };

  const removePageFromBulk = () => {
    const folderIds = folders.map(selectId);
    const contentIds = contentItems.map(selectId);

    setBulkSelection((currentValue) =>
      currentValue.filter(
        (item) =>
          (item.type === SelectionTypes.Folder && !folderIds.includes(item.id)) ||
          (item.type === SelectionTypes.Content && !contentIds.includes(item.id)),
      ),
    );
  };

  const clearAll = () => {
    setBulkSelection([]);
    setCurrentSelection([]);
  };

  const selectItem = (item: FolderableItem<ContentId, ContentName> | FolderableFolder) => {
    setCurrentSelection((currentValue) => {
      if (currentValue.findIndex((value) => value.id === item.id) > -1) {
        return currentValue;
      }

      return [...currentValue, marshallItem(item)];
    });
  };

  const selectBulk = () => {
    setCurrentSelection(bulkSelection);
  };

  /** Deselects all items whenever the user changes their current folder */
  useEffect(() => {
    clearAll();
  }, [folderId]);

  const context: SelectionContextState<ContentId, ContentName> = {
    bulk: bulkSelection,
    toggleBulkSelection,
    addPageToBulk,
    removePageFromBulk,
    active: currentSelection,
    selectItem,
    selectBulk,
    clearAll,
  };

  return context;
}
